<template>
  <div>
    <v-card class="my-1">
      <v-card-title>Yearly Payroll Details by Individual</v-card-title>
      <v-row class="px-4">
        <v-col>
          <v-row class="px-4">
            <v-col cols="3">
              <v-autocomplete
                v-model="year"
                v-validate="'required'"
                label="Year"
                :loading="loading"
                :items="years"
                item-value="id"
                :error-messages="errors.collect('year')"
                data-vv-name="year"
                data-vv-as="year"
              />
            </v-col>

            <v-col>
              <v-autocomplete
                v-model="selectedEmployees"
                :items="employees"
                :loading="loading"
                label="Employees"
                multiple
                clearable
                deletable-chips
                chips
                small-chips
                hide-selected
                :error-messages="errors.collect('employee')"
                item-text="person.name"
                item-value="employee_uid"
                data-vv-name="employee"  
                data-vv-as="employee"
              />
            </v-col>
          </v-row>

          <v-row class="px-4">
            <v-col>
              <v-autocomplete
                v-model="selectedGroups"
                :items="groupOptions"
                label="Company Groups"
                item-text="text"
                item-value="value"
                multiple
                chips
                small-chips
                deletable-chips
                hide-selected
                clearable
              />
            </v-col>
          </v-row>

          <v-row class="px-4">
            <v-col>
              <v-autocomplete
                v-model="selectedCompanies"
                v-validate="'required'"
                :items="companyOptions"
                label="Companies"
                item-text="name"
                item-value="id"
                multiple
                chips
                small-chips
                deletable-chips
                hide-selected
                :error-messages="errors.collect('company')"
                data-vv-name="company"  
                data-vv-as="company"
                clearable
              />
            </v-col>
          </v-row>
        </v-col>

        <v-col
          cols="2"
          class="ma-auto"
        >
          <v-row
            v-if="progressing" 
            class="text-center"
          >
            <v-col cols="12">
              <div>
                <span class="font-weight-bold">Downloading</span>
              </div>
              <v-progress-circular
                :rotate="360"
                :size="50"
                :width="5"
                :value="progressbar"
                color="teal"
              >
                {{ progressbar }}
              </v-progress-circular>
            </v-col>
          </v-row>

          <v-row class="text-center">
            <v-col cols="12">
              <v-btn
                class="mx-3"
                :loading="progressing"
                color="primary"
                @click="generate"
              >
                generate
              </v-btn>
            </v-col>
          </v-row> 
        </v-col>
      </v-row> 
    </v-card> 
  </div>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      years: {
        type: Array,
        required: true,
      },
      companies: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        loading: false,
        progressing: false,
        year: null,
        progressbar: 0,
        selectedGroups: [],
        selectedCompanies: [],
        selectedEmployees: [],
        employees: [],
      }
    },
    computed: {
      groupOptions () {
        if (!this.companies) return []
        const groups = new Set()

        this.companies.forEach(company => {
          const groupVal = company.organization_lookup_uid
          if (!groupVal) return

          groups.add(company.group)
        })

        return Array.from(groups).map(group => ({
          text: `${(group || {}).value} Group`,
          value: group.lookup_uid,
        }))
      },
      filteredCompanies () {
        if (!this.selectedGroups.length) return this.companies

        return (this.companies || []).filter(company =>
          this.selectedGroups.includes(company.organization_lookup_uid)
        )
      },
      companyOptions () {
        const companies = (this.filteredCompanies || []).map(company => ({
          name: company.name,
          id: company.id,
        }))

        return [
          { name: 'All Companies', id: 'ALL' },
          ...companies,
        ]
      },
      selectedEmployeeObjects () {
        return this.selectedEmployees.map(empUid =>
          this.employees.find(emp => emp.employee_uid === empUid)
        )
      },
    },
    watch: {
      selectedGroups () {
        const validIds = (this.filteredCompanies || []).map(company => company.id)

        if (this.selectedCompanies.includes('ALL')) {
          this.selectedCompanies = [...validIds]
        } else {
          this.selectedCompanies = this.selectedCompanies.filter(id =>
            validIds.includes(id)
          )
        }
      },
      selectedEmployeeObjects (newEmployees) {
        if (newEmployees.length > 0) {
          let matchedCompanies = []
          newEmployees.forEach(emp => {
            emp.company_employees.forEach(compEmp => {
              const filtered = this.companies.filter(company => company.id === compEmp.company_uid)

              filtered.forEach(company => {
                if (!matchedCompanies.some(comp => comp.id === company.id)) {
                  matchedCompanies.push(company)
                }
              })
            })
          })
          this.filteredCompanies = matchedCompanies
        } else {
          this.filteredCompanies = this.companies
          this.selectedCompanies = []
        }
      },
      selectedCompanies (newVal) {
        const last = newVal[newVal.length - 1]
        
        // If last selected is 'ALL', keep only 'ALL'
        if (last === 'ALL' && newVal.length > 1) {
          // Replace with only 'ALL' after current update cycle
          this.$nextTick(() => {
            this.selectedCompanies = ['ALL']
          })
        } else if (newVal.includes('ALL') && newVal.length > 1) {
          // Remove 'ALL' if other company is selected after it
          this.$nextTick(() => {
            this.selectedCompanies = newVal.filter(id => id !== 'ALL')
          })
        }
      },
    },
    created () {
      this.loading = true
        
      this.$api.hrisEmployees().list()
        .then((res) => {
          this.employees = _.orderBy(res.data, ['person.name'], ['asc'])

          this.loading = false
        })
    },
    methods: {
      generate () {
        this.$validator.validate('year')
          .then(isYearValid => {
            if (!isYearValid) return

            if (!this.selectedCompanies.length && !this.selectedEmployees.length) {
              this.$snack({ color: 'error', message: 'Please select at least one company or employee' })
              return
            }
            return this.$validator.validateAll()
          })
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.payrollReports().yearlyPayrollDetailsByIndividual(this.prepareFormData(), {
              onDownloadProgress: (progressEvent) => {
                this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
              },
            }).then(res => {
              let blob = new Blob([res.data], { type: res.data.type })
              let link = document.createElement('a')
              link.href = window.URL.createObjectURL(blob)
              link.setAttribute('download', 'Yearly Payroll Details by Individual ' + this.year + ' [' + this.$moment().format('YYYY-MM-DD h.mma') + '].xlsx')
              link.click()

              this.progressing = false
              this.progressbar = 0

              this.$snack({ message: 'Report downloaded successfully' })
            }).catch(() => {
              this.progressing = false

              this.$snack({ color: 'error', message: 'Error! Please contact support' })
            })
          })
      },
      prepareFormData () {
        return {
          year: this.year,
          employees: this.selectedEmployees,
          companies: this.selectedCompanies.includes('ALL') ? (
            this.filteredCompanies.filter(company => {
              return company.id !== 'ALL'
            }).map(company => {
              return company.id
            })
          ) : this.selectedCompanies,
        }
      },
    },
  }
</script>
